import Timeline from '@/components/ui/timeline/Timeline';
import type { Issue, IssueEvent } from '@/types';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import type React from 'react';
import { useMemo } from 'react';

interface IssueEventsTabProps {
  issue: Issue;
}

const IssueEventsTab = ({ issue }: IssueEventsTabProps) => {
  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'middleman_offline':
      case 'client_offline':
        return <ErrorIcon sx={{ fontSize: 24, color: 'error.main' }} />;
      case 'middleman_recovered':
        return <InfoIcon sx={{ fontSize: 24, color: 'success.main' }} />;
      case 'client_recovered':
        return <InfoIcon sx={{ fontSize: 24, color: 'success.main' }} />;
      case 'machine_offline':
        return <WarningIcon sx={{ fontSize: 24, color: 'warning.main' }} />;
      default:
        return <InfoIcon sx={{ fontSize: 24, color: 'info.main' }} />;
    }
  };

  const getEventTypeLabel = (eventType: string) => {
    switch (eventType) {
      case 'hardware_failure':
        return 'Hardware Failure';
      case 'client_offline':
        return 'Client Offline';
      case 'middleman_offline':
        return 'Middleman Offline';
      case 'client_recovered':
        return 'Client Recovered';
      case 'middleman_recovered':
        return 'Middleman Recovered';
      default:
        return eventType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      local: date.toLocaleString(),
      utc: `${date.toISOString().replace('T', ' ').substring(0, 19)} UTC`,
    };
  };

  const renderEventCard = (event: IssueEvent, isResolved: boolean) => {
    const eventDate = formatDate(event.created_at);
    const hasDetails = event.details && Object.keys(event.details).length > 0;
    const detailEntries = hasDetails ? Object.entries(event.details) : [];

    return (
      <Card
        key={event.id}
        sx={{
          mb: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: isResolved ? 'success.main' : 'error.main',
          borderLeftWidth: 4,
          backgroundColor: isResolved ? 'success.50' : 'error.50',
        }}
      >
        <CardContent sx={{ p: 2 }}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {getEventTypeLabel(event.event_type)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Machine {event.atm_id}
              </Typography>
            </Box>
          </Box>

          {/* Event Time */}
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, textTransform: 'uppercase' }}
            >
              Event Time
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {eventDate.local}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {eventDate.utc}
            </Typography>
          </Box>

          {/* Details Section */}
          {hasDetails && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontWeight: 600, textTransform: 'uppercase' }}
              >
                Details ({detailEntries.length})
              </Typography>
              <Box sx={{ overflowX: 'auto', mt: 1 }}>
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableBody>
                    {detailEntries.map(([key, value]) => (
                      <TableRow
                        key={key}
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: 'grey.50' },
                          '& td': { border: 'none', py: 0.5 },
                        }}
                      >
                        <TableCell sx={{ width: '40%', verticalAlign: 'top' }}>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{ wordBreak: 'break-word' }}
                          >
                            {key === 'LastHeartbeatCreatedAtServerTime'
                              ? 'Detected at'
                              : key === 'OfflineFor'
                                ? 'Recovered at'
                                : key}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right', verticalAlign: 'top' }}>
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                          >
                            {String(value)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const timelineItems = useMemo(() => {
    const items: Array<{
      name: string;
      icon: React.ReactNode;
      content: React.ReactNode;
      timestamp: number;
      isResolved: boolean;
    }> = [];

    // Add triggered events
    if (issue.triggered_by_events && issue.triggered_by_events.length > 0) {
      const triggeredEvents = [...issue.triggered_by_events].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

      triggeredEvents.forEach((event) => {
        items.push({
          name: `Triggered: ${getEventTypeLabel(event.event_type)}`,
          icon: getEventIcon(event.event_type),
          content: renderEventCard(event, false),
          timestamp: new Date(event.created_at).getTime(),
          isResolved: false,
        });
      });
    }

    // Add resolved events
    if (issue.resolved_by_events && issue.resolved_by_events.length > 0) {
      const resolvedEvents = [...issue.resolved_by_events].sort(
        (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

      resolvedEvents.forEach((event) => {
        items.push({
          name: `Resolved: ${getEventTypeLabel(event.event_type)}`,
          icon: getEventIcon(event.event_type),
          content: renderEventCard(event, true),
          timestamp: new Date(event.created_at).getTime(),
          isResolved: true,
        });
      });
    }

    // Sort all items by timestamp (chronological order)
    items.sort((a, b) => a.timestamp - b.timestamp);

    return items.map(({ name, icon, content }) => ({
      name,
      icon: icon as React.ReactElement,
      content: content as React.ReactElement,
    }));
  }, [issue]);

  const triggeredCount = issue.triggered_by_events?.length || 0;
  const resolvedCount = issue.resolved_by_events?.length || 0;
  const totalCount = triggeredCount + resolvedCount;

  return (
    <Box mt={2}>
      <Stack direction="row" spacing={2} mb={3}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
            Triggered Events
          </Typography>
          <Typography variant="h6" color="error.main">
            {triggeredCount}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
            Resolved Events
          </Typography>
          <Typography variant="h6" color="success.main">
            {resolvedCount}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
            Total Events
          </Typography>
          <Typography variant="h6" color="text.primary">
            {totalCount}
          </Typography>
        </Box>
      </Stack>

      {totalCount > 0 ? (
        <Timeline
          items={timelineItems}
          dotProps={{
            sx: {
              backgroundColor: 'white',
              p: 1,
              border: '2px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            },
          }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No events found for this issue.
        </Typography>
      )}
    </Box>
  );
};

export default IssueEventsTab;
